/**
 *首页 vm
 *@author xujinjiang
 *@date 2022/3/1 17:35
 */
import { BaseViewModel } from '@/viewmodel/BaseViewModel';
import { HuohuaProducts } from '@/constant/HuohuaProducts';
import { PartnersIcon } from '@/constant/PartnersIcon';
import { reactive, ref, watch } from 'vue';
import { ApplyRules } from '@/constant/ApplyRules';
import { ElForm } from 'element-plus';
import { WebsiteServiceImpl } from '@/service/WebsiteServiceImpl';
import { CommonUtil } from '@/utils/CommonUtil';
import { ObserverService } from '@/service/ObserverService';
import { UrlConfig } from '@/config/UrlConfig';
import { Debug } from '@/constant/Debug';
import { RouterUtil } from '@/utils/RouterUtil';
import axios from 'axios';
import { VerificationCode } from '@/utils/VerificationCode';
import { InstitutionServiceImpl } from '@/service/InstitutionServiceImpl';

type FormInstance = InstanceType<typeof ElForm>;

export class HomeViewModel extends BaseViewModel {
  public huohuaProducts = HuohuaProducts;
  public partnersIcon = PartnersIcon;
  public problems = [];
  public applyObject = reactive({
    companyName: '',
    phone: '',
    name: '',
    email: '',
    province: '',
    city: '',
    district: ''
  }); // 新增开发者对象
  public rules = reactive(ApplyRules);
  public formSize = ref('');
  public ruleFormRef = ref<FormInstance>();
  public btnLoading = false;

  public changePage = false;
  public showPhoneFooter = false;
  public showDialog = false;
  public imgCodeSrc = ''; // 图形验证码地址
  public smsCode = ''; // 短信验证码
  public imgCodeText = ''; // 图形验证码
  public errorTip = ''; // 错误信息
  public verificationCode = '';
  public provinceList = []; // 省列表
  public cityList = []; // 市列表
  public areaList = []; // 区列表

  created() {
    this.getProblems();
  }

  mounted() {
    this.getProvinceList();
    if (window.innerWidth <= 1440) {
      this.changePage = true;
    }
    if (window.innerWidth <= 735) {
      this.showPhoneFooter = true;
    }
    this.setBannerCss();
    this.resize();
  }

  /**
   * 获取省列表
   */
  private async getProvinceList() {
    try {
      const result = await InstitutionServiceImpl.getInstance().getProvinceInfo();
      this.provinceList = result.province;
    } catch (e) {
      console.error('获取省列表失败：', e);
    }
  }

  /**
   * 选择省份
   */
  changeProvince() {
    this.cityList = [];
    this.areaList = [];
    this.applyObject.city = '';
    this.applyObject.district = '';
    this.getCityList();
  }

  /**
   * 获取市列表
   * @private
   */
  private async getCityList() {
    if (this.applyObject.province === '') {
      return;
    }
    try {
      const result: any = await InstitutionServiceImpl.getInstance().getCityInfo(
        this.applyObject.province
      );
      this.cityList = result.city;
    } catch (e) {
      console.error('获取省列表失败：', e);
    }
  }

  /**
   * 选择市
   */
  changeCity() {
    this.applyObject.district = '';
    this.areaList = [];
    this.getAreaList();
  }

  /**
   * 获取区列表
   * @private
   */
  private async getAreaList() {
    if (this.applyObject.city === '') {
      return;
    }
    try {
      const result: any = await InstitutionServiceImpl.getInstance().getDistrictInfo(
        this.applyObject.city
      );
      this.areaList = result.district;
    } catch (e) {
      console.error('获取省列表失败：', e);
    }
  }

  enterKeyEvent(event: any) {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      this.submit().then();
    }
  }

  setVerificationCode() {
    const verificationCode = VerificationCode.generateCode(120, 40);
    if (!verificationCode) {
      return;
    }
    this.imgCodeSrc = verificationCode.data;
    this.verificationCode = verificationCode.code;
  }

  private getProblems() {
    if (Debug) {
      this.problems = require('../../../public/qa/qa.json');
      return;
    }
    axios.get(`${UrlConfig.qaPath}/qa.json`).then((res) => {
      this.problems = res.data;
    });
  }

  private resize() {
    window.addEventListener('resize', () => {
      if (window.innerWidth <= 1440) {
        this.changePage = true;
      }
      if (window.innerWidth > 1440) {
        this.changePage = false;
      }
      if (window.innerWidth <= 735) {
        this.showPhoneFooter = true;
      }
      if (window.innerWidth > 735) {
        this.showPhoneFooter = false;
      }
      this.setBannerCss();
    });
  }

  private setBannerCss() {
    const banner = document.getElementById('banner');
    const header = document.getElementById('header');
    let bannerW = 760;
    if (banner) {
      bannerW = banner.clientWidth;
    }
    if (header) {
      header.style.height = bannerW * (1520 / 3840) + 'px';
    }
  }

  public scroll() {
    const el = document.querySelector('.website-home') as any;
    if (el.scrollTop === 0) {
      ObserverService.getInstance().getShowWhiteHeadObserver().next(false);
    }
    if (el.scrollTop > 0) {
      ObserverService.getInstance().getShowWhiteHeadObserver().next(true);
    }
  }

  public goApply() {
    const el = document.querySelector('#applyDeveloper') as any;
    el.scrollIntoView({
      behavior: 'smooth'
    });
  }

  public productFocus(item: any) {
    item.showDescription = true;
  }

  public productBlur(item: any) {
    item.showDescription = false;
  }

  public setShowDescription(item: any, event: any) {
    event.preventDefault();
    item.showDescription = !item.showDescription;
  }

  public openDoc() {
    RouterUtil.openDocQA(this.$router);
  }

  getImageCode() {
    this.setVerificationCode();
  }

  /**
   * 获取验证码
   */
  async sendSms() {
    console.log(this.imgCodeText, this.verificationCode);
    if (!this.imgCodeText || this.imgCodeText != this.verificationCode) {
      this.errorTip = '图形验证码不正确';
      return;
    }
    this.errorTip = '';
    try {
      await WebsiteServiceImpl.getInstance().getVerificationCode(this.applyObject.phone);
    } catch (e: any) {
      CommonUtil.errorMessage(e.message);
      this.btnLoading = false;
    }
    CommonUtil.cutDownTimer('#sendSms');
  }
  /**
   * 确认框中点提交
   */
  async submit() {
    if (!this.imgCodeText) {
      this.errorTip = '图形验证码不能为空';
      return;
    }
    if (this.imgCodeText.toLowerCase() != this.verificationCode) {
      this.errorTip = '图形验证码不正确';
      return;
    }
    // if (!this.smsCode) {
    //   this.errorTip = '短信验证码不能为空';
    //   return;
    // }
    await this.applyDeveloper();
    this.showDialog = false;
    window.removeEventListener('keyup', this.enterKeyEvent, true);
  }
  /**
   * 提交申请成为开发者
   * @param ref
   */
  public submitForm(ref: FormInstance | undefined) {
    if (!ref) {
      return;
    }
    ref.validate(async (valid) => {
      if (!valid) {
        return false;
      }
      console.log(this.applyObject);
      this.showDialog = true;
      window.addEventListener('keyup', this.enterKeyEvent, true);
      this.setVerificationCode();
    });
  }

  resetFields(ref: FormInstance | undefined) {
    if (!ref) {
      return;
    }
    ref.resetFields();
  }

  /**
   * 申请成为开发者
   */
  public async applyDeveloper() {
    this.btnLoading = true;
    try {
      await WebsiteServiceImpl.getInstance().applyDeveloper(this.applyObject, this.smsCode);
      CommonUtil.successMessage('申请成功，工作人员将在 1 个工作日内与您联系');
      this.btnLoading = false;
      this.applyObject.name = '';
      this.applyObject.phone = '';
      this.applyObject.companyName = '';
      this.applyObject.email = '';
      this.applyObject.province = '';
      this.applyObject.city = '';
      this.applyObject.district = '';
      this.cityList = [];
      this.areaList = [];
      // @ts-ignore
      this.resetFields(this.ruleFormRef);
    } catch (e: any) {
      CommonUtil.errorMessage(e.message);
      this.btnLoading = false;
    }
  }

  private checkPhone = (rule?: any, value?: any, callback?: any) => {
    const reg = /^1[345789]\d{9}$/;
    if (!reg.test(value)) {
      callback(new Error('请输入正确11位手机号'));
    } else {
      callback();
    }
  };

  /**
   * 验证邮箱
   */
  private checkEmail = (rule?: any, value?: any, callback?: any) => {
    console.log(value);
    const reg = /^[A-Za-z\d]+([-_.][A-Za-z\d]+)*@([A-Za-z\d]+[-.])+[A-Za-z\d]{2,4}$/;
    if (!reg.test(value)) {
      callback(new Error('请输入正确邮箱'));
    } else {
      callback();
    }
  };

  public toHuoHua() {
    window.open(`${UrlConfig.websiteDomain}`);
  }
  public policy() {
    window.open(`${UrlConfig.privacy}`);
  }
  public about() {
    window.open(`${UrlConfig.about}`);
  }

  public leaveBtn() {
    const el = document.querySelector('.confirm') as any;
    el.blur();
  }

  clearInfo() {
    this.errorTip = '';
    this.imgCodeText = '';
    const element = document.getElementById('sendSms') as HTMLInputElement;
    if (!element) {
      return;
    }
    element.innerHTML = '获取短信验证码';
  }
}
